import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addMaterialUsageRecipient,
  listMaterialUsageRecipients,
  removeMaterialUsageRecipient,
  type MaterialUsageRecipientContext,
} from "@/lib/service/materialUsageRecipientsClient";
import { ghostBtnClass, inputClass, primaryBtnClass } from "./serviceShared";

export function ServiceMaterialUsageRecipientsSection() {
  const [context, setContext] = useState<MaterialUsageRecipientContext | null>(null);
  const [plantId, setPlantId] = useState("");
  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const result = await listMaterialUsageRecipients();
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setContext(result.data);
    setError(null);
    setPlantId((current) => current || result.data.plants[0]?.id || "");
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const plantRecipients = useMemo(
    () => (context?.recipients ?? []).filter((row) => row.plant_id === plantId),
    [context, plantId],
  );
  const takenUserIds = useMemo(
    () => new Set(plantRecipients.map((row) => row.user_id).filter((id): id is string => !!id)),
    [plantRecipients],
  );
  const memberChoices = (context?.members ?? []).filter((member) => !takenUserIds.has(member.user_id));

  async function onAddUser() {
    if (!plantId || !userId) return;
    setBusy(true);
    const result = await addMaterialUsageRecipient({ plant_id: plantId, user_id: userId });
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setUserId("");
    await reload();
  }

  async function onAddEmail() {
    if (!plantId || !email.trim()) return;
    setBusy(true);
    const result = await addMaterialUsageRecipient({ plant_id: plantId, email: email.trim() });
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setEmail("");
    await reload();
  }

  async function onRemove(id: string) {
    setBusy(true);
    const result = await removeMaterialUsageRecipient(id);
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    await reload();
  }

  const domains = context?.approved_domains ?? [];

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3 dark:border-zinc-700 dark:bg-zinc-900">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Materials usage mail</h3>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Each plant has its own list. Add a member without an opt-in, or a group mailbox on an approved domain.
        </p>
      </div>

      {error ? (
        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
      ) : null}

      {!context ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Loading plants…</p>
      ) : context.plants.length === 0 ? (
        <p className="text-sm text-gray-600 dark:text-zinc-400">No active plants yet.</p>
      ) : (
        <>
          <label className="block text-sm text-gray-700 dark:text-zinc-300">
            Plant
            <select
              className={`${inputClass} mt-1`}
              value={plantId}
              onChange={(event) => setPlantId(event.target.value)}
            >
              {context.plants.map((plant) => (
                <option key={plant.id} value={plant.id}>
                  {plant.name}
                </option>
              ))}
            </select>
          </label>

          <ul className="space-y-1 text-sm text-gray-800 dark:text-zinc-200">
            {plantRecipients.length === 0 ? (
              <li className="text-gray-500 dark:text-zinc-400">No recipients for this plant.</li>
            ) : (
              plantRecipients.map((row) => (
                <li key={row.id} className="flex items-center justify-between gap-2">
                  <span>
                    {row.display_name}
                    {row.email ? ` · ${row.email}` : ""}
                    {row.kind === "address" ? " · group mailbox" : ""}
                  </span>
                  <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void onRemove(row.id)}>
                    Remove
                  </button>
                </li>
              ))
            )}
          </ul>

          <div className="flex flex-wrap items-end gap-2">
            <label className="block min-w-48 flex-1 text-sm text-gray-700 dark:text-zinc-300">
              Member
              <select
                className={`${inputClass} mt-1`}
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
              >
                <option value="">Select a member</option>
                {memberChoices.map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.display_name}
                    {member.email ? ` (${member.email})` : ""}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className={primaryBtnClass} disabled={busy || !userId} onClick={() => void onAddUser()}>
              Add member
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <label className="block min-w-48 flex-1 text-sm text-gray-700 dark:text-zinc-300">
              Email on an approved domain
              <input
                className={`${inputClass} mt-1`}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={domains[0] ? `service@${domains[0]}` : "service@your-domain.com"}
                autoComplete="off"
              />
            </label>
            <button
              type="button"
              className={primaryBtnClass}
              disabled={busy || !email.trim()}
              onClick={() => void onAddEmail()}
            >
              Add email
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            {domains.length > 0
              ? `Approved domains: ${domains.join(", ")}`
              : "No approved email domains are set for this organization, so group mailboxes cannot be added yet."}
          </p>
        </>
      )}
    </section>
  );
}
